import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import routes from 'constants/routes';
import { useCashDeposits } from '../../hooks/useCashDeposits';
import { DepositStatus } from '../../types';
import { formatCashAmount } from '../BranchCashSummary';

// ─── Badge de estado ──────────────────────────────────────────────────────────

const STATUS_COLORS: Record<DepositStatus, 'warning' | 'success' | 'default'> = {
	BORRADOR: 'warning',
	CONFIRMADO: 'success',
	ANULADO: 'default',
};

const STATUS_LABELS: Record<DepositStatus, string> = {
	BORRADOR: 'Borrador',
	CONFIRMADO: 'Confirmado',
	ANULADO: 'Anulado',
};

export function DepositStatusChip({ status }: { status: DepositStatus }): JSX.Element {
	return (
		<Chip
			label={STATUS_LABELS[status]}
			size="small"
			color={STATUS_COLORS[status]}
			variant="outlined"
			sx={{ fontSize: 11 }}
		/>
	);
}

// ─── Filtro de estado ─────────────────────────────────────────────────────────

interface StatusFilterProps {
	value: DepositStatus | undefined;
	onChange: (status: DepositStatus | undefined) => void;
}

function StatusFilter({ value, onChange }: StatusFilterProps): JSX.Element {
	const ALL: Array<{ label: string; value: DepositStatus | undefined }> = [
		{ label: 'Todos', value: undefined },
		{ label: 'Borradores', value: 'BORRADOR' },
		{ label: 'Confirmados', value: 'CONFIRMADO' },
		{ label: 'Anulados', value: 'ANULADO' },
	];
	return (
		<Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
			{ALL.map((opt) => (
				<Chip
					key={opt.label}
					label={opt.label}
					size="small"
					variant={value === opt.value ? 'filled' : 'outlined'}
					onClick={() => onChange(opt.value)}
					sx={{ cursor: 'pointer' }}
				/>
			))}
		</Stack>
	);
}

// ─── Tabla ────────────────────────────────────────────────────────────────────

function formatDateTime(isoString: string): string {
	return new Date(isoString).toLocaleString('es', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

interface DepositActsListProps {
	/** Si se pasa, filtra por esa sucursal. Si no, muestra todas (vista admin). */
	branch?: string;
}

/**
 * Tabla de actas de depósito. Reutilizable tanto como tab en el detalle de una
 * caja como en la vista global de superadmin `/cajas/actas`.
 */
export function DepositActsList({ branch }: DepositActsListProps): JSX.Element {
	const [statusFilter, setStatusFilter] = React.useState<DepositStatus | undefined>(undefined);

	const { deposits, pagination, loading, loadingMore, error, cachedAt, loadMore, hasMore, refetch } = useCashDeposits(
		{ branch, status: statusFilter },
		25,
	);

	return (
		<>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
				<StatusFilter value={statusFilter} onChange={setStatusFilter} />
				<Box display="flex" alignItems="center" gap={1.5}>
					<CachedBadge cachedAt={cachedAt} />
					{pagination && (
						<Typography variant="caption" color="text.secondary">
							{pagination.total} acta{pagination.total !== 1 ? 's' : ''}
						</Typography>
					)}
					<Button size="small" variant="text" onClick={refetch} sx={{ textTransform: 'none', minWidth: 0 }}>
						↺
					</Button>
				</Box>
			</Box>

			{loading ? (
				<Skeleton variant="rectangular" height={200} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : deposits.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No hay actas{statusFilter ? ` con estado "${STATUS_LABELS[statusFilter]}"` : ''}.
				</Typography>
			) : (
				<>
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>#</TableCell>
									{!branch && <TableCell>Sucursal</TableCell>}
									<TableCell>Fecha</TableCell>
									<TableCell>Estado</TableCell>
									<TableCell align="right">Monto depositado</TableCell>
									<TableCell align="right">Recibos</TableCell>
									<TableCell>Ref. bancaria</TableCell>
									<TableCell>Registrado por</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{deposits.map((act) => (
									<TableRow key={act.id} hover>
										<TableCell>
											<Typography variant="body2" fontWeight={600}>
												#{act.id}
											</Typography>
										</TableCell>
										{!branch && <TableCell>{act.branch}</TableCell>}
										<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(act.createdAt)}</TableCell>
										<TableCell>
											<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
												<DepositStatusChip status={act.status} />
												{(act.vaultAmount ?? 0) > 0 && (
													<Chip
														label="Caja fuerte"
														size="small"
														color="secondary"
														variant="outlined"
														sx={{ fontSize: 11 }}
													/>
												)}
											</Box>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={600}>
												{formatCashAmount(act.depositedAmount, act.currency)}
											</Typography>
										</TableCell>
										<TableCell align="right">{act.receipts?.length ?? '—'}</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{act.bankReference ?? '—'}
											</Typography>
										</TableCell>
										<TableCell>{act.registeredBy}</TableCell>
										<TableCell>
											<Button
												size="small"
												variant="text"
												component={RouterLink}
												to={routes.branchCash.depositDetail(act.id)}
												sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
											>
												Ver acta
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{hasMore && (
						<Box display="flex" justifyContent="center" mb={2}>
							<Chip
								label={loadingMore ? 'Cargando…' : `Cargar más (${deposits.length} de ${pagination?.total ?? 0})`}
								onClick={loadMore}
								variant="outlined"
								size="small"
								sx={{ cursor: 'pointer' }}
							/>
						</Box>
					)}
				</>
			)}
		</>
	);
}
